import firebase from "firebase/compat/app";
import {
  doc,
  getFirestore,
  onSnapshot,
  setDoc,
  Unsubscribe,
  writeBatch,
} from "firebase/firestore";
import { DB_NODES } from "../../../../../db/constants/nodeNames";
import { AppUser, UpdatableAppUserJSON } from "../../../models/user/user";

class UserApi {
  private _authObserver: firebase.Unsubscribe | undefined;
  private _userDataObserver: Unsubscribe | undefined;
  private _userCityDataObserver: Unsubscribe | undefined;

  async dangerouslyPermanentlyDeleteMe(userId: string): Promise<boolean> {
    let success = true;

    try {
      const db = getFirestore();
      const phoneSharingDoc = doc(db, DB_NODES.PHONE_NUMBER_SHARING, userId);
      const pushTokensDoc = doc(db, DB_NODES.PUSH_NOTIFICATION_TOKENS, userId);
      const userAlbumsDoc = doc(db, DB_NODES.USER_ALBUMS, userId);
      const userDoc = doc(db, DB_NODES.USERS, userId);

      const batch = writeBatch(db);
      batch.delete(phoneSharingDoc);
      batch.delete(pushTokensDoc);
      batch.delete(userAlbumsDoc);
      batch.delete(userDoc);

      await batch.commit();
    } catch (e) {
      success = false;
      console.error(e);
    }

    return success;
  }

  async updateUser(userId: string, json: UpdatableAppUserJSON) {
    const db = getFirestore();
    const document = doc(db, DB_NODES.USERS, userId);
    await setDoc(document, json, { merge: true });
  }

  registerUserObserver(callbackfn: (_: AppUser | null) => void) {
    this._authObserver = firebase
      .auth()
      .onAuthStateChanged((firebaseUser: any) => {
        // No user is logged in with firebase
        if (!firebaseUser) {
          callbackfn(null);
          return;
        }

        // User is logged in with firebase
        try {
          // Convert firebase user to app user with firebase auth id and phone
          // number
          const appUser = AppUser.fromFirebaseUser(
            firebaseUser as firebase.User,
          );

          // Attempt to add user information from firebase database
          const db = getFirestore();
          const userDocument = doc(db, DB_NODES.USERS, firebaseUser.uid);

          this._userDataObserver = onSnapshot(
            userDocument,
            (userDocSnapShot) => {
              const userData = userDocSnapShot.data();

              if (userData) {
                appUser.addUpdatableAppUserJSON(
                  userData as UpdatableAppUserJSON,
                );

                const userCityDocument = doc(
                  db,
                  DB_NODES.GEO_AREA_CITIES,
                  userData?.city_id,
                );

                this._userCityDataObserver = onSnapshot(
                  userCityDocument,
                  (cityDocSnapShot) => {
                    const userCityData = cityDocSnapShot.data();

                    if (userCityData) {
                      appUser.updateIsComplete({
                        name: userData.name,
                        countryId: userData.country_id,
                        stateId: userData.state_id,
                        cityId: userData.city_id,
                        cityHasNeighbourhoods: userCityData.hasNeighbourhoods,
                        neighbourhoodId: userData.neighbourhood_id,
                      });
                    }

                    callbackfn(appUser);
                  },
                );
              } else {
                callbackfn(appUser);
              }
            },
          );
        } catch (e) {
          console.error(e);
        }
      });
  }

  unregisterUserObserver() {
    this._authObserver?.();
    this._userDataObserver?.();
    this._userCityDataObserver?.();
  }

  signOut() {
    firebase.auth().signOut();
  }
}

export default new UserApi();
